import { API } from "aws-amplify";
import axios from "axios";

// Standard chunk size for all devices (S3 minimum is 5MB for multipart uploads)
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

// Detect mobile device
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
         window.innerWidth <= 768;
};

export const uploadFunctions = {
  // Upload thumbnail to S3
  uploadThumbnail: async (thumbnailFile) => {
    try {
      const thumbnailResponse = await API.post(
        "user",
        `/admin/upload-class-videos`,
        {
          body: {
            operation: "GET_THUMBNAIL_URL",
            fileName: thumbnailFile.name,
            contentType: thumbnailFile.type,
          },
        }
      );

      if (!thumbnailResponse.presignedUrl || !thumbnailResponse.key) {
        throw new Error("Invalid response for thumbnail upload URL");
      }

      const uploadResponse = await axios.put(thumbnailResponse.presignedUrl, thumbnailFile, {
        headers: {
          "Content-Type": thumbnailResponse.isWebP ? "image/webp" : thumbnailFile.type,
        },
        timeout: 30000, // 30 seconds timeout
      });

      if (uploadResponse.status !== 200) {
        throw new Error("Thumbnail upload failed");
      }

      // Log conversion info if available
      if (thumbnailResponse.isWebP) {
        console.log(`Thumbnail converted to WebP: ${thumbnailResponse.originalFileName} → ${thumbnailResponse.finalFileName}`);
      } else {
        console.log(`Thumbnail uploaded in original format: ${thumbnailResponse.originalFileName}`);
      }

      return {
        key: thumbnailResponse.key,
        thumbnailUrl: thumbnailResponse.thumbnailUrl,
        originalFileName: thumbnailResponse.originalFileName,
        finalFileName: thumbnailResponse.finalFileName,
        isWebP: thumbnailResponse.isWebP
      };
    } catch (error) {
      console.error("Thumbnail upload error:", error);
      throw new Error(`Failed to upload thumbnail: ${error.message}`);
    }
  },

  // Initiate multipart upload
  initiateMultipartUpload: async (videoDetails) => {
    try {
      const response = await API.post(
        "user",
        `/admin/upload-class-videos`,
        {
          body: {
            operation: "INITIATE_UPLOAD",
            ...videoDetails,
          },
        }
      );

      if (!response.uploadId || !response.key) {
        throw new Error("Invalid response from initiate upload");
      }

      return { uploadId: response.uploadId, key: response.key };
    } catch (error) {
      console.error("Initiate upload error:", error);
      throw new Error(`Failed to initiate multipart upload: ${error.message}`);
    }
  },

  // Upload chunk using XMLHttpRequest (better for mobile)
  uploadChunkWithXHR: async (chunk, partNumber, presignedUrl) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          const eTag = xhr.getResponseHeader('etag') || 
                      xhr.getResponseHeader('ETag') || 
                      xhr.getResponseHeader('Etag');
          
          if (!eTag) {
            reject(new Error(`No ETag received for part ${partNumber}`));
            return;
          }
          
          console.log(`XHR upload successful for part ${partNumber}, ETag: ${eTag}`);
          resolve({
            PartNumber: partNumber,
            ETag: eTag.replace(/^["']|["']$/g, ""),
          });
        } else {
          reject(new Error(`XHR upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error(`XHR network error for part ${partNumber}`));
      };
      
      xhr.ontimeout = function() {
        reject(new Error(`XHR timeout for part ${partNumber}`));
      };
      
      xhr.timeout = 60000; // 60 seconds
      xhr.send(chunk);
    });
  },

  // Upload chunk using Axios
  uploadChunkWithAxios: async (chunk, partNumber, presignedUrl) => {
    try {
      console.log(`Trying Axios upload for part ${partNumber}...`);
      
      const response = await axios.put(presignedUrl, chunk, {
        headers: {
          "Content-Type": "application/octet-stream",
        },
        timeout: 60000, // 60 seconds timeout
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      
      if (response.status >= 200 && response.status < 300) {
        const eTag = response.headers["etag"] || 
                     response.headers["ETag"] || 
                     response.headers["Etag"];
        
        if (!eTag) {
          throw new Error(`No ETag received for part ${partNumber}`);
        }
        
        console.log(`Axios upload successful for part ${partNumber}, ETag: ${eTag}`);
        return {
          PartNumber: partNumber,
          ETag: eTag.replace(/^["']|["']$/g, ""),
        };
      } else {
        throw new Error(`Axios upload failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Axios upload failed for part ${partNumber}:`, error);
      throw error;
    }
  },

  // Direct upload method for mobile (simple and reliable)
  uploadChunkDirect: async (chunk, partNumber, presignedUrl) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.open('PUT', presignedUrl, true);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          const eTag = xhr.getResponseHeader('etag') || 
                      xhr.getResponseHeader('ETag') || 
                      xhr.getResponseHeader('Etag');
          
          if (!eTag) {
            reject(new Error(`No ETag received for part ${partNumber}`));
            return;
          }
          
          console.log(`Direct upload successful for part ${partNumber}, ETag: ${eTag}`);
          resolve({
            PartNumber: partNumber,
            ETag: eTag.replace(/^["']|["']$/g, ""),
          });
        } else {
          reject(new Error(`Direct upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error(`Direct upload network error for part ${partNumber}`));
      };
      
      xhr.ontimeout = function() {
        reject(new Error(`Direct upload timeout for part ${partNumber}`));
      };
      
      xhr.timeout = 120000; // 2 minutes timeout for mobile
      xhr.send(chunk);
    });
  },

  // Upload chunk using FormData (alternative method)
  uploadChunkWithFormData: async (chunk, partNumber, presignedUrl) => {
    try {
      console.log(`Trying FormData upload for part ${partNumber}...`);
      
      const response = await axios.put(presignedUrl, chunk, {
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        timeout: 60000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      
      if (response.status >= 200 && response.status < 300) {
        const eTag = response.headers["etag"] || 
                     response.headers["ETag"] || 
                     response.headers["Etag"];
        
        if (!eTag) {
          throw new Error(`No ETag received for part ${partNumber}`);
        }
        
        console.log(`FormData upload successful for part ${partNumber}, ETag: ${eTag}`);
        return {
          PartNumber: partNumber,
          ETag: eTag.replace(/^["']|["']$/g, ""),
        };
      } else {
        throw new Error(`FormData upload failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error(`FormData upload failed for part ${partNumber}:`, error);
      throw error;
    }
  },

  // Test presigned URL accessibility
  testPresignedUrl: async (presignedUrl) => {
    try {
      const response = await axios.head(presignedUrl, {
        timeout: 10000,
      });
      return response.status >= 200 && response.status < 300;
    } catch (error) {
      console.warn("Presigned URL test failed:", error.message);
      return false;
    }
  },

  // Upload individual chunk
  uploadChunk: async (chunk, partNumber, uploadId, key) => {
    try {
      console.log(`Starting upload for part ${partNumber}, chunk size: ${chunk.size} bytes`);
      
      // Get the upload URL
      const urlResponse = await API.post("user", `/admin/upload-class-videos`, {
        body: {
          operation: "GET_UPLOAD_URL",
          uploadId,
          partNumber,
          key,
        },
      });
  
      if (!urlResponse.presignedUrl) {
        throw new Error("No presigned URL received");
      }

      console.log(`Got presigned URL for part ${partNumber}:`, urlResponse.presignedUrl.substring(0, 100) + '...');
      
      // For mobile, use a simple direct approach
      if (isMobile()) {
        console.log(`Using direct upload for mobile part ${partNumber}...`);
        return await uploadFunctions.uploadChunkDirect(chunk, partNumber, urlResponse.presignedUrl);
      }
      
      // For desktop, use Axios
      console.log(`Using Axios for desktop part ${partNumber}...`);
      return await uploadFunctions.uploadChunkWithAxios(chunk, partNumber, urlResponse.presignedUrl);
      
    } catch (error) {
      console.error(`Error uploading part ${partNumber}:`, error);
      throw new Error(`Failed to upload part ${partNumber}: ${error.message}`);
    }
  },
  // Complete multipart upload
  completeMultipartUpload: async (uploadId, parts, key) => {
    try {
      if (!parts || parts.length === 0) {
        throw new Error("No parts provided for completion");
      }

      const response = await API.post(
        "user",
        `/admin/upload-class-videos`,
        {
          body: {
            operation: "COMPLETE_UPLOAD",
            uploadId,
            parts,
            key,
          },
        }
      );

      if (!response || !response.videoUrl) {
        throw new Error("Invalid response from complete upload");
      }

      return response;
    } catch (error) {
      console.error("Complete upload error:", error);
      throw new Error(`Failed to complete multipart upload: ${error.message}`);
    }
  },

  // Update metadata in DynamoDB
  updateMetadata: async (metadata) => {
    try {
      await API.post("user", `/admin/upload-class-videos`, {
        body: {
          operation: "UPDATE_METADATA",
          ...metadata,
        },
      });
    } catch (error) {
      console.error("Update metadata error:", error);
      throw new Error(`Failed to update metadata: ${error.message}`);
    }
  },

  // Handle the complete upload process with mobile optimizations
  handleCompleteUpload: async (formData, setUploadProgress) => {
    // Add timeout wrapper for the entire upload process
    const uploadWithTimeout = async () => {
      try {
        console.log("Starting complete upload process...");
        console.log("Form data:", {
          videoSize: formData.video.size,
          videoType: formData.video.type,
          videoName: formData.video.name,
        });

        // Upload thumbnail
        console.log("Uploading thumbnail...");
        const thumbnailResult = await uploadFunctions.uploadThumbnail(
          formData.thumbnail
        );
        console.log("Thumbnail upload completed");

        // Initiate multipart upload
        console.log("Initiating multipart upload...");
        const { uploadId, key } = await uploadFunctions.initiateMultipartUpload({
          videoType: formData.videoType,
          partNo: formData.partNo,
          songName: formData.songName,
          title: formData.title,
          choreographer: formData.choreographer,
          playlist: formData.playlist,
          contentType: formData.video.type,
          fileName: formData.video.name,
        });
        console.log("Multipart upload initiated, uploadId:", uploadId);

        // Use smaller chunk size for mobile devices
        const chunkSize = CHUNK_SIZE;
        const chunks = Math.ceil(formData.video.size / chunkSize);
        const uploadedParts = [];

        console.log(`Uploading ${chunks} chunks with size ${chunkSize} bytes`);

        for (let i = 0; i < chunks; i++) {
          console.log(`Processing chunk ${i + 1}/${chunks}...`);
          
          const start = i * chunkSize;
          const end = Math.min(start + chunkSize, formData.video.size);
          const chunk = formData.video.slice(start, end);

          try {
            console.log(`Starting upload for chunk ${i + 1}...`);
            
            const part = await uploadFunctions.uploadChunk(
              chunk,
              i + 1,
              uploadId,
              key
            );
            uploadedParts.push(part);
            console.log(`Chunk ${i + 1} uploaded successfully`);
            
            // Update progress more frequently on mobile
            const progress = ((i + 1) / chunks) * 100;
            console.log(`Upload progress: ${progress.toFixed(1)}%`);
            setUploadProgress(progress);
            
            // Add small delay on mobile to prevent overwhelming the network
            if (i < chunks - 1) {
              console.log("Adding delay for mobile device...");
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error(`Failed to upload chunk ${i + 1}:`, error);
            
            // Provide user-friendly error messages
            let userMessage = error.message;
            if (error.message.includes('Network Error') || error.message.includes('timeout')) {
              userMessage = 'Network connection lost. Please check your internet connection and try again.';
            } else if (error.message.includes('timeout')) {
              userMessage = 'Upload is taking too long. Please try again with a better internet connection.';
            } else if (error.message.includes('network')) {
              userMessage = 'Network error. Please check your connection and try again.';
            }
            
            throw new Error(`Upload failed at chunk ${i + 1}: ${userMessage}`);
          }
        }

        console.log("All chunks uploaded, completing multipart upload...");

        // Complete multipart upload
        const completionResponse = await uploadFunctions.completeMultipartUpload(
          uploadId,
          uploadedParts,
          key
        );
        console.log("Multipart upload completed");

        // Update metadata
        console.log("Updating metadata...");
        const thumbnailUrl = thumbnailResult.thumbnailUrl;
        const videoUrl = completionResponse.videoUrl;

        await uploadFunctions.updateMetadata({
          videoType: formData.videoType,
          partNo: formData.partNo,
          songName: formData.songName,
          title: formData.title,
          choreographer: formData.choreographer,
          playlist: formData.playlist,
          videoUrl: videoUrl,
          thumbnailUrl: thumbnailUrl,
        });
        console.log("Metadata updated successfully");

        // Return success status and data
        console.log("Upload process completed successfully");
        return {
          success: true,
          data: {
            videoType_partNo: `${formData.videoType}_${formData.partNo}`,
            songName: formData.songName,
            title: formData.title,
            choreographer: formData.choreographer,
            playlist: formData.playlist,
            videoUrl: videoUrl,
            thumbnailUrl: thumbnailUrl,
            uploadDate: Date.now(), // Add current date if needed for "time ago" functionality
          },
        };
      } catch (error) {
        console.error("Error during complete upload process:", error);
        throw error;
      }
    };

    // Add timeout for the entire upload process
    const timeoutDuration = 600000; // 10 minutes
    console.log(`Setting upload timeout to ${timeoutDuration / 1000} seconds`);

    return Promise.race([
      uploadWithTimeout(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout - the upload took too long to complete')), timeoutDuration)
      )
    ]);
  },
};

export default uploadFunctions;
